import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';

let uiNavSelectUid = 0;

export interface UiNavSelectOption {
  value: string;
  label: string;
}

/**
 * Átomo: select estilizado al estilo de `ui-menubar__item`.
 * Trigger con icono opcional + texto + chevron.
 * Panel desplegable con opciones de solo texto.
 *
 * El panel usa `position: absolute; top: 100%` dentro del wrapper
 * con `position: relative`, igual que `ui-menubar__submenu`.
 */
@Component({
  selector: 'ui-nav-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './nav-select.component.html',
  styleUrl: './nav-select.component.scss',
  host: {
    class: 'ui-nav-select-host',
  },
})
export class UiNavSelectComponent {
  private readonly hostEl = inject(ElementRef<HTMLElement>);

  /** Lista de opciones disponibles */
  readonly options = input<readonly UiNavSelectOption[]>([]);

  /** Valor seleccionado actualmente. Soporta `[(value)]` two-way binding */
  readonly value = model<string | null>(null);

  /** Texto mostrado en el trigger cuando no hay selección */
  readonly placeholder = input('Seleccionar');

  /** URL de icono SVG/PNG mostrado a la izquierda del trigger (opcional) */
  readonly iconSrc = input<string | null>(null);

  /** Etiqueta accesible del control */
  readonly ariaLabel = input('Seleccionar');

  /** Deshabilita el control completamente */
  readonly disabled = input(false);

  /** Emite el `value` de la opción seleccionada al cambiar */
  readonly optionSelect = output<string>();

  protected readonly panelId = `ui-nav-select-panel-${++uiNavSelectUid}`;
  protected readonly isOpen = signal(false);

  protected readonly selectedLabel = computed(() => {
    const v = this.value();
    if (!v) return null;
    return this.options().find((o) => o.value === v)?.label ?? null;
  });

  protected toggle(event: MouseEvent): void {
    event.stopPropagation();
    if (this.disabled()) return;
    this.isOpen.update((v) => !v);
  }

  protected select(option: UiNavSelectOption, event: MouseEvent): void {
    event.stopPropagation();
    if (this.disabled()) return;
    this.value.set(option.value);
    this.optionSelect.emit(option.value);
    this.isOpen.set(false);
  }

  protected close(): void {
    this.isOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent): void {
    if (!this.isOpen()) return;
    if (this.hostEl.nativeElement.contains(ev.target as Node)) return;
    this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) this.close();
  }
}
